import React, { useState, useEffect, useCallback } from 'react';
import { 
  getCollaborators, 
  deleteCollaborator as deleteCollaboratorAPI,
  createCollaborator as createCollaboratorAPI 
} from '../services/api';
import { CollaboratorEntity, CreateCollaboratorRequest } from '../types';
import Toast from './Toast';
import CollaboratorForm from './CollaboratorForm';
import DeleteCollaboratorModal from './DeleteCollaboratorModal';
import { getInitials } from '../utils/stringUtils';
import DataTable, { Column } from './DataTable';
import { PrimaryButton } from './buttons';
import { useAccount } from '../contexts/AccountContext';

const CollaboratorList: React.FC = () => {
  const [collaborators, setCollaborators] = useState<CollaboratorEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCollaborator, setEditingCollaborator] = useState<CollaboratorEntity | null>(null);
  const [deletingCollaborator, setDeletingCollaborator] = useState<CollaboratorEntity | null>(null);
  
  // Account context for manager account selection
  const { selectedAccount, isManager } = useAccount();
  
  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
    show: boolean;
  }>({ message: '', type: 'success', show: false });

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type, show: true });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, show: false }));
  };

  const loadCollaborators = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCollaborators(selectedAccount?.id);
      
      // The API service now always returns an array
      setCollaborators(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load collaborators:', err);
      setCollaborators([]); // Ensure collaborators is always an array
      setError(err instanceof Error ? err.message : 'Failed to load collaborators');
      showToast('Failed to load collaborators', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedAccount?.id]);

  useEffect(() => {
    loadCollaborators();
  }, [loadCollaborators]);

  const handleCreateCollaborator = async (data: CreateCollaboratorRequest) => {
    try {
      // The form now handles accountId, so just pass through the data
      const newCollaborator = await createCollaboratorAPI(data);
      
      // Validate the collaborator object before adding to list
      if (newCollaborator && newCollaborator.id && newCollaborator.name) {
        setCollaborators(prev => [newCollaborator, ...prev]);
        setShowCreateForm(false);
        showToast('Collaborator created successfully!', 'success');
      } else {
        console.error('Invalid collaborator object received:', newCollaborator);
        showToast('Error: Invalid collaborator data received from server', 'error');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create collaborator';
      showToast(errorMessage, 'error');
      throw err; // Re-throw so the form can handle it
    }
  };

  const handleDeleteCollaborator = async (collaborator: CollaboratorEntity) => {
    try {
      await deleteCollaboratorAPI(collaborator.id);
      setCollaborators(prev => Array.isArray(prev) ? prev.filter(c => c.id !== collaborator.id) : []);
      setDeletingCollaborator(null);
      showToast('Collaborator deleted successfully!', 'success');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete collaborator';
      showToast(errorMessage, 'error');
    }
  };

  // Define table columns
  const columns: Column<CollaboratorEntity>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (collaborator: CollaboratorEntity) => (
        <div className="font-medium text-content">{collaborator.name}</div>
      ),
    },
    // Only show account column for managers
    ...(isManager ? [{
      key: 'accountName',
      header: 'Artist Account',
      sortable: true,
      render: (collaborator: CollaboratorEntity) => (
        <div className="text-content-secondary">
          {collaborator.accountName ? (
            <span className="inline-flex items-center">
              <span className="w-6 h-6 rounded-full bg-[#14B8A6] flex items-center justify-center text-xs text-white mr-2">
                {getInitials(collaborator.accountName)}
              </span>
              {collaborator.accountName}
            </span>
          ) : (
            <span className="text-content-muted italic">Unassigned</span>
          )}
        </div>
      ),
    }] : []) as Column<CollaboratorEntity>[],
    {
      key: 'email',
      header: 'Email',
      sortable: true,
      render: (collaborator: CollaboratorEntity) => (
        collaborator.email ? (
          <div className="text-content-secondary">{collaborator.email}</div>
        ) : (
          <span className="text-content-muted">—</span>
        )
      ),
    },
    {
      key: 'pro',
      header: 'PRO',
      sortable: true,
      render: (collaborator: CollaboratorEntity) => (
        collaborator.pro ? (
          <div className="text-content-secondary">{collaborator.pro}</div>
        ) : (
          <span className="text-content-muted">—</span>
        )
      ),
    },
    {
      key: 'ipiNumber',
      header: 'IPI Number',
      sortable: true,
      render: (collaborator: CollaboratorEntity) => (
        collaborator.ipiNumber ? (
          <div className="text-content-secondary">{collaborator.ipiNumber}</div>
        ) : (
          <span className="text-content-muted">—</span>
        )
      ),
    },
  ];

  // Render actions for each row
  const renderActions = (collaborator: CollaboratorEntity) => (
    <>
      <button
        onClick={() => setDeletingCollaborator(collaborator)}
        className="p-2 text-content-muted hover:text-state-error hover:bg-red-500/10 rounded-lg transition-colors"
        title="Delete collaborator"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    </>
  );

  return (
    <div className="min-h-screen bg-[#18181B]">
      <div className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="bg-surface-secondary border border-border-subtle rounded-lg p-4 sm:p-6 mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Collaborators</h1>
              <p className="text-sm sm:text-base text-[#6B7280]">
                {selectedAccount && <span className="text-[#14B8A6] font-medium">{selectedAccount.name}</span>}
                {selectedAccount && ' • '}Quick selection in splits
              </p>
            </div>
            <PrimaryButton onClick={() => setShowCreateForm(true)} className="w-full sm:w-auto">
              + Add Collaborator
            </PrimaryButton>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-surface-secondary border border-red-500/40 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-state-error flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-state-error text-sm">{error}</p>
                <button
                  onClick={loadCollaborators}
                  className="text-xs text-red-300 hover:text-red-200 underline mt-1"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl overflow-hidden shadow-card">
          <DataTable
            data={collaborators}
            columns={columns}
            searchPlaceholder="Search by name, email, or IPI..."
            searchKeys={['name', 'email', 'ipiNumber'] as (keyof CollaboratorEntity)[]}
            renderActions={renderActions}
            onRowClick={(collaborator) => setEditingCollaborator(collaborator)}
            loading={loading}
            emptyState={{
            icon: (
              <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            ),
            title: 'No collaborators yet',
            description: 'Add your first collaborator to get started.',
            action: (
              <PrimaryButton onClick={() => setShowCreateForm(true)}>
                Add Your First Collaborator
              </PrimaryButton>
            ),
          }}
          itemsPerPageOptions={[10, 20, 50]}
          defaultItemsPerPage={10}
        />
        </div>

        {/* Create Collaborator Modal */}
        {showCreateForm && (
          <CollaboratorForm
            isOpen={showCreateForm}
            onClose={() => setShowCreateForm(false)}
            onSubmit={handleCreateCollaborator}
            title="Add New Collaborator"
          />
        )}

        {/* Edit Collaborator Modal */}
        {editingCollaborator && (
          <CollaboratorForm
            isOpen={!!editingCollaborator}
            onClose={() => setEditingCollaborator(null)}
            onSubmit={async (data: CreateCollaboratorRequest) => {
              // The CollaboratorForm will handle the update API call
              setEditingCollaborator(null);
              await loadCollaborators(); // Reload the list
              showToast('Collaborator updated successfully!', 'success');
            }}
            initialData={editingCollaborator}
            title="Edit Collaborator"
            isEdit
          />
        )}

        {/* Delete Confirmation Modal */}
        {deletingCollaborator && (
          <DeleteCollaboratorModal
            collaborator={deletingCollaborator}
            isOpen={!!deletingCollaborator}
            onClose={() => setDeletingCollaborator(null)}
            onConfirm={() => handleDeleteCollaborator(deletingCollaborator)}
          />
        )}

        {/* Toast Notifications */}
        {toast.show && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={hideToast}
          />
        )}
      </div>
    </div>
  );
};

export default CollaboratorList;
